"use client";

import * as React from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Gauge } from "lucide-react";

import { profileApi } from "@/lib/api/profile";

/**
 * Widget de uso en el sidebar: barra de gasto LLM del mes (spent/cap) con color
 * por umbral, y un cartel de aviso si la cuota está agotada o cerca (>=80%).
 * Enlaza a la página "Mis límites".
 */
export function UsageWidget({ collapsed }: { collapsed?: boolean }) {
  const { data, isError } = useQuery({
    queryKey: ["profile-usage"],
    queryFn: profileApi.getMyUsage,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  if (isError || !data) return null;

  const pct = Math.min(100, Math.max(0, data.percent));
  const barColor =
    pct >= 100 ? "var(--red, #dc2626)" : pct >= 80 ? "var(--amber, #d97706)" : "var(--green, #16a34a)";
  const warn = data.paused || pct >= 80;

  if (collapsed) {
    // Colapsado: solo un icono con color según estado, enlaza a /limites.
    return (
      <Link
        href="/limites"
        title={`Uso IA: ${pct.toFixed(0)}%`}
        className="flex justify-center py-2"
        aria-label="Ver límites de uso"
      >
        <Gauge className="h-4 w-4" style={{ color: barColor }} />
      </Link>
    );
  }

  return (
    <Link href="/limites" className="block px-4 py-3" aria-label="Ver límites de uso">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium" style={{ color: "var(--gray-600)" }}>
          Uso de IA (mes)
        </span>
        <span className="text-xs tabular-nums" style={{ color: "var(--gray-500)" }}>
          {pct.toFixed(0)}%
        </span>
      </div>
      <div
        className="h-1.5 rounded-full overflow-hidden"
        style={{ backgroundColor: "var(--gray-200)" }}
      >
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: barColor }}
        />
      </div>
      {warn && (
        <div
          className="mt-2 flex items-start gap-1.5 rounded-md px-2 py-1.5 text-[11px] leading-tight"
          style={{
            backgroundColor: data.paused ? "var(--red-bg, #fee2e2)" : "var(--amber-bg, #fef3c7)",
            color: data.paused ? "var(--red, #b91c1c)" : "var(--amber, #92400e)",
          }}
        >
          <AlertTriangle className="h-3 w-3 mt-px shrink-0" />
          <span>
            {data.paused
              ? "Cuota mensual agotada. La IA está bloqueada."
              : `Te queda el ${(100 - pct).toFixed(0)}% de tu cuota.`}
          </span>
        </div>
      )}
    </Link>
  );
}
